/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import dayjs from 'dayjs';
import {
  COMMON_COLORS,
  PayloadContent,
  Tag,
  TrendingOverviewChartWidget,
} from '@neteera/neteera-web-nextgen';

import { deepEqual } from 'src/utils/comparators';
import type { PropsFromRedux } from './Connector';
import {
  getChartConfig,
  getLeftAxis,
  getRightAxis,
  getReferenceLines,
  getLegendConfig,
  xAxisTickFormatter,
  tooltipTimeFormatter,
  getTooltipConfig,
} from './config';
import {
  hydrateTooltipPayload,
  getVisibleReferenceLines,
  getVisibleTrendLines,
} from './utils';
import { AlertLog } from 'src/types/alerts';
import { ChartPayloadItem, PATIENT_STATUS } from './types';
import AlertCard from 'src/components/Sidebars/components/AlertCard';
import { StyledAlertCard } from 'src/routes/IndividualDashboard/components/DashboardTab/TrendingOverviewWidget/styled';

export type Props = Omit<PropsFromRedux, 'getPatientMetricData'> & {
  intl: IntlShape;
  getPatientMetricData?: PropsFromRedux['getPatientMetricData'];
};

const TrendingOverviewWidget = ({
  intl,
  patientId,
  startTime,
  endTime,
  getPatientMetricData,
  data,
  hrBaseline,
  rrBaseline,
  stepSize,
  unit,
  positionChanges,
  timeFrame,
  dataPoints,
  isLoading,
}: Props) => {
  const [activeData, setActiveData] = useState<string | null>(null);
  const [hiddenData, setHiddenData] = useState<string[]>([]);
  const config = useMemo(() => getChartConfig(intl, messages), [intl]);
  const leftAxis = useMemo(() => getLeftAxis(intl, messages), [intl]);
  const rightAxis = useMemo(() => getRightAxis(intl, messages), [intl]);
  const referenceLines = useMemo(
    () => getReferenceLines(hrBaseline, rrBaseline, positionChanges),
    [hrBaseline, rrBaseline, positionChanges],
  );
  const legendConfig = getLegendConfig(
    config,
    intl,
    messages,
    activeData,
    hiddenData,
    stepSize,
    unit,
  );
  const tooltipConfig = useMemo(
    () => getTooltipConfig(config, intl, messages),
    [config, intl],
  );
  const naValueMessage = useMemo(() => intl.formatMessage(messages.na), [intl]);
  const visibleReferenceLines = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return getVisibleReferenceLines(referenceLines, activeData, hiddenData);
  }, [referenceLines, activeData, hiddenData, isLoading]);
  const visibleTrendLines = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return getVisibleTrendLines(activeData, hiddenData);
  }, [activeData, hiddenData, isLoading]);

  const headerContent = (
    <>
      <span className="text-xs text-neteera-teal-500">{timeFrame}</span>
      <div className="flex flex-row items-center gap-2">
        <Tag size="sm" bgColor={COMMON_COLORS.teal200} label={dataPoints} />
        <span className="text-base">
          {intl.formatMessage(messages.dataPoints)}
        </span>
      </div>
    </>
  );
  const renderAlertTooltip = (activeAlert: Record<'value', unknown>) => {
    const hoveredAlerts = activeAlert.value as AlertLog[];
    const { id, deviceId, patientId } = hoveredAlerts?.[0] ?? {};
    return id && deviceId && patientId ? (
      <StyledAlertCard>
        <AlertCard
          background={COMMON_COLORS.teal200}
          key={id}
          alertId={id}
          deviceId={deviceId}
          patientId={patientId}
          config={{
            allowAlertDelete: false,
            displayPatientName: false,
            displaySiteLocation: false,
            displaySimilarNotifications: hoveredAlerts.length > 1,
          }}
          similarNotifications={{
            count: hoveredAlerts.length - 1,
            intervalSize: stepSize,
            intervalUnit: unit,
          }}
          onPatientNameClick={() => console.log('qqq')}
          onDeleteAlertClick={() => console.log('wwww')}
        />
      </StyledAlertCard>
    ) : (
      <></>
    );
  };

  const renderTooltip = (payload?: ChartPayloadItem[]) => {
    const hydratedPayload = hydrateTooltipPayload(
      payload,
      tooltipConfig,
      naValueMessage,
    );

    const dotPayload = payload?.[0]?.payload;
    const patientStatus =
      (dotPayload?.patientStatus as PATIENT_STATUS) || PATIENT_STATUS.NO_DATA;
    return (
      <>
        <span>{tooltipTimeFormatter(dotPayload?.time as string)}</span>
        <span
          className={patientStatus === PATIENT_STATUS.IN_BED ? '' : 'font-bold'}
        >
          {intl.formatMessage(messages[patientStatus])}
        </span>
        <PayloadContent payload={hydratedPayload} config={tooltipConfig} />
      </>
    );
  };

  const handleLineClick = (
    dataKey: string,
    _lineProps: unknown,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setActiveData(activeData ? null : dataKey);
  };

  const handleAlertClick = (
    alertData: { dataKey: string },
    event: React.MouseEvent,
  ) => {
    const { dataKey: alertDataKey } = alertData;
    const lineDataKey = alertDataKey.replace('Alert', '');

    event.stopPropagation();
    event.preventDefault();
    setActiveData(activeData ? null : lineDataKey);
  };

  const handleChartClick = (_chartState: unknown, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setActiveData(null);
  };

  const handleLegendClick = (dataKey: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (hiddenData?.includes(dataKey)) {
      setHiddenData(hiddenData?.filter(item => item !== dataKey));
    } else {
      setHiddenData([...hiddenData, dataKey]);
    }
  };

  useEffect(() => {
    if (patientId && getPatientMetricData) {
      getPatientMetricData({
        patientId,
        startTime: dayjs.utc(startTime),
        endTime: dayjs.utc(endTime),
      });
    }
  }, [patientId, startTime, endTime, getPatientMetricData]);

  return (
    <TrendingOverviewChartWidget
      title={intl.formatMessage(messages.title)}
      isLoading={isLoading}
      data={
        isLoading
          ? []
          : data.map(elem => ({
              ...elem,
              motion:
                elem?.motion === null
                  ? intl.formatMessage(messages.na)
                  : elem?.motion,
            }))
      }
      headerContent={headerContent}
      config={config}
      legendConfig={legendConfig}
      onLegendClick={handleLegendClick}
      leftAxis={leftAxis}
      rightAxis={rightAxis}
      xAxisTickFormatter={value => xAxisTickFormatter(value, stepSize, unit)}
      xAxisTicks={data
        .filter(elem => elem.time.includes(':00'))
        .map(elem => elem.time)}
      referenceLines={visibleReferenceLines}
      trendLines={visibleTrendLines}
      alertTooltipBgColor={COMMON_COLORS.teal200}
      alertTooltipWrapperClassName="p-0"
      renderTooltip={renderTooltip}
      renderAlertTooltip={renderAlertTooltip}
      onLineClick={handleLineClick}
      onAlertClick={handleAlertClick}
      onChartClick={handleChartClick}
    />
  );
};

const messages = defineMessages({
  title: {
    id: 'TrendingOverviewWidget.index.title',
    defaultMessage: 'Trending Overview',
  },
  bpm: {
    id: 'TrendingOverviewWidget.index.bpm',
    defaultMessage: 'BPM',
  },
  brpm: {
    id: 'TrendingOverviewWidget.index.brpm',
    defaultMessage: 'BRPM',
  },
  heartRate: {
    id: 'TrendingOverviewWidget.index.heartRate',
    defaultMessage: 'Heart Rate',
  },
  hr: {
    id: 'TrendingOverviewWidget.index.hr',
    defaultMessage: 'HR',
  },
  hrBaseline: {
    id: 'TrendingOverviewWidget.index.hrBaseline',
    defaultMessage: 'HR Baseline',
  },
  respiratoryRate: {
    id: 'TrendingOverviewWidget.index.respiratoryRate',
    defaultMessage: 'Respiratory Rate',
  },
  rr: {
    id: 'TrendingOverviewWidget.index.rr',
    defaultMessage: 'RR',
  },
  rrBaseline: {
    id: 'TrendingOverviewWidget.index.rrBaseline',
    defaultMessage: 'RR Baseline',
  },
  motion: {
    id: 'TrendingOverviewWidget.index.motion',
    defaultMessage: 'Motion Index',
  },
  motionShort: {
    id: 'TrendingOverviewWidget.index.motionShort',
    defaultMessage: 'Motion',
  },
  dataPoints: {
    id: 'TrendingOverviewWidget.index.dataPoints',
    defaultMessage: 'Data Points',
  },
  positionChange: {
    id: 'TrendingOverviewWidget.index.positionChange',
    defaultMessage: 'Position Change',
  },
  respIeRatio: {
    id: 'TrendingOverviewWidget.index.respIeRatio',
    defaultMessage: 'Resp. I/E Ratio',
  },
  respAmplitude: {
    id: 'TrendingOverviewWidget.index.respAmplitude',
    defaultMessage: 'Resp. Amplitude',
  },
  na: {
    id: 'TrendingOverviewWidget.index.na',
    defaultMessage: 'N/A',
  },
  inBed: {
    id: 'TrendingOverviewWidget.index.inBed',
    defaultMessage: 'In bed',
  },
  noData: {
    id: 'TrendingOverviewWidget.index.noData',
    defaultMessage: 'No Data: N/A',
  },
  noDataOutOfBed: {
    id: 'TrendingOverviewWidget.index.noDataOutOfBed',
    defaultMessage: 'No Data: Out of Bed',
  },
  noDataDisconnectedDevice: {
    id: 'TrendingOverviewWidget.index.noDataDisconnectedDevice',
    defaultMessage: 'No Data: Disconnected Device',
  },
});

export default injectIntl(
  React.memo(TrendingOverviewWidget, (oldProps, newProps) =>
    deepEqual(oldProps, newProps),
  ),
);
